import { DYNAMIC_FIELD_MODES, STATIC_LINKABLE_FIELDS } from "../components/common/constants";
import { getDynamicFields } from "../components/recommendations/services";

const clientHierarchyFieldsCache = new Map();
const clientHierarchyFieldsRequests = new Map();

const getClientHierarchyFieldsCacheKey = (projectId, staticFieldKey) => `${projectId}::${staticFieldKey}`;

export const loadClientHierarchyFields = async (projectId, staticFieldKey) => {
    if (!projectId) return [];

    const cacheKey = getClientHierarchyFieldsCacheKey(projectId, staticFieldKey);

    if (clientHierarchyFieldsCache.has(cacheKey)) {
        return clientHierarchyFieldsCache.get(cacheKey);
    }

    if (clientHierarchyFieldsRequests.has(cacheKey)) {
        return clientHierarchyFieldsRequests.get(cacheKey);
    }

    const request = getDynamicFields(buildClientHierarchyFieldsQueryParams(projectId, staticFieldKey))
        .then(response => {
            const fields = getClientHierarchyFields(response?.data || response, staticFieldKey);
            clientHierarchyFieldsCache.set(cacheKey, fields);
            clientHierarchyFieldsRequests.delete(cacheKey);
            return fields;
        })
        .catch(error => {
            clientHierarchyFieldsRequests.delete(cacheKey);
            throw error;
        });

    clientHierarchyFieldsRequests.set(cacheKey, request);
    return request;
};

export const getCachedClientHierarchyFields = (projectId, staticFieldKey) =>
    clientHierarchyFieldsCache.get(getClientHierarchyFieldsCacheKey(projectId, staticFieldKey));

export const buildClientHierarchyFieldsQueryParams = (projectId, staticFieldKey) => ({
    project_id: projectId,
    field_mode: DYNAMIC_FIELD_MODES.CLIENT_HIERARCHY,
    linked_static_field: staticFieldKey
});

export const getClientHierarchyFields = (dynamicFieldsResponse, staticFieldKey) => {
    const fields = dynamicFieldsResponse?.recommendation_custom_fields || dynamicFieldsResponse?.list || [];
    const flatFields = Array.isArray(fields) ? fields : Object.values(fields).flat();

    return flatFields.filter(
        field =>
            field?.field_mode === DYNAMIC_FIELD_MODES.CLIENT_HIERARCHY &&
            field?.rules?.linked_static_field === staticFieldKey
    );
};

export const normalizeLinkedDynamicFieldsToMap = linkedDynamicFields => {
    if (!linkedDynamicFields) return {};

    if (Array.isArray(linkedDynamicFields)) {
        return linkedDynamicFields.reduce((acc, item) => {
            if (item?.field_name) {
                acc[item.field_name] = item.value ?? "";
            }
            return acc;
        }, {});
    }

    if (typeof linkedDynamicFields === "object") {
        return { ...linkedDynamicFields };
    }

    return {};
};

export const formatLinkedDynamicFieldsForApi = linkedDynamicFieldsMap => {
    if (!linkedDynamicFieldsMap || !Object.keys(linkedDynamicFieldsMap).length) {
        return [];
    }

    return Object.entries(linkedDynamicFieldsMap)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([field_name, value]) => ({ field_name, value }));
};

export const getLinkedDynamicFieldDisplayValue = (field, value) => {
    if (!value) return "-";
    const option = field?.field_options?.find(opt => opt.value === value || opt.key === value);
    return option?.value || value;
};

export const getLinkedDynamicFieldOptionColor = (field, value) => {
    if (!value) return "";
    const option = field?.field_options?.find(opt => opt.value === value || opt.key === value);
    return option?.color || "";
};

export const applySubsystemLinkedDynamicFields = (currentDynamicFields, subsystemItem, dynamicFieldsResponse) => {
    const next = { ...(currentDynamicFields || {}) };
    const clientHierarchyFields = getClientHierarchyFields(dynamicFieldsResponse, STATIC_LINKABLE_FIELDS.SUB_SYSTEM.id);

    clientHierarchyFields.forEach(field => {
        const linkedMap = normalizeLinkedDynamicFieldsToMap(subsystemItem?.linked_dynamic_fields);
        next[field.field_name] = subsystemItem?.id ? linkedMap[field.field_name] ?? "" : "";
    });

    return next;
};

export const getLinkedDynamicTableConfig = (config = {}) =>
    Object.fromEntries(
        Object.entries(config).filter(
            ([key, value]) => key.startsWith("linked_dynamic_field__") || value?.isLinkedDynamicField
        )
    );

export const mergeLinkedDynamicTableConfig = (targetConfig = {}, sourceConfig = {}) => ({
    ...targetConfig,
    ...getLinkedDynamicTableConfig(sourceConfig)
});

export const mergeLinkedDynamicTableKeys = (baseKeys = [], dynamicKeys = []) => {
    if (!dynamicKeys.length) return baseKeys;

    const keys = baseKeys.filter(key => !key.startsWith("linked_dynamic_field__"));
    const createdAtIndex = keys.indexOf("created_at");

    if (createdAtIndex >= 0) {
        keys.splice(createdAtIndex, 0, ...dynamicKeys);
    } else {
        keys.push(...dynamicKeys);
    }

    return keys;
};

export const buildClientHierarchyTableColumns = (clientHierarchyFields, baseKeys, baseConfig) => {
    const dynamicKeys = [];
    const config = { ...baseConfig };

    clientHierarchyFields.forEach(field => {
        const key = `linked_dynamic_field__${field.field_name}`;
        dynamicKeys.push(key);
        config[key] = {
            isVisible: true,
            label: field.label,
            class: "width-160px",
            type: field.field_type || "string",
            searchKey: `linked_dynamic_fields.${field.field_name}`,
            hasWildCardSearch: true,
            hasCommonSearch: true,
            getListTable:
                field.field_type === "boolean"
                    ? ["Yes", "No"]
                    : field?.field_options?.map(item => item.value) || [],
            commonSearchKey: "linked_dynamic_fields",
            commonSearchObjectKey: field.field_name,
            isLinkedDynamicField: true,
            accessor: rowData => {
                const linkedMap = normalizeLinkedDynamicFieldsToMap(rowData?.linked_dynamic_fields);
                return getLinkedDynamicFieldDisplayValue(field, linkedMap[field.field_name]);
            }
        };
    });

    const keys = [...baseKeys];
    const createdAtIndex = keys.indexOf("created_at");
    if (createdAtIndex >= 0) {
        keys.splice(createdAtIndex, 0, ...dynamicKeys);
    } else {
        keys.push(...dynamicKeys);
    }

    return { keys, config };
};
